/** value из auto_add_rules_types */
export const AUTO_ADD_TYPE_VALUE = {
	WHOLE_ORDER: 'whole_order',
	FOR_DISHES: 'for_dishes',
}

export function resolveAutoAddTypeValue(rule) {
	if (!rule) return AUTO_ADD_TYPE_VALUE.WHOLE_ORDER
	if (rule.auto_add_rule_type_value) {
		return String(rule.auto_add_rule_type_value)
	}
	const typeId = Number(rule.auto_add_rule_type_id)
	if (typeId === 2) return AUTO_ADD_TYPE_VALUE.FOR_DISHES
	return AUTO_ADD_TYPE_VALUE.WHOLE_ORDER
}

/** Правила из Redux, подходящие под текущий order_type_sub_id КЦ. */
export function filterAutoAddRulesByOrderTypeSubId(rules, orderTypeSubId) {
	if (!Array.isArray(rules) || !rules.length) return []
	const subId = Number(orderTypeSubId)
	if (!subId) return []
	return rules.filter((rule) => {
		const ids = (rule.order_type_sub_ids || []).map(Number).filter((id) => id > 0)
		if (!ids.length) return false
		return ids.includes(subId)
	})
}

function isActiveLine(product) {
	return Number(product?.status) === 0 || Number(product?.status) > 0
}

// Роль строки — по связи с блюдом, а не по типу товара: компонентом конструктора бывает любой товар
function isModifierLine(product) {
	if (!product) return false
	if (String(product.product_type_value ?? '').toUpperCase() === 'MODIFIER') return true
	if (Number(product.modifier_parent_id) > 0) return true
	if (product.parent_cheque_product_id != null && product.parent_cheque_product_id !== '') return true
	return product.parent_transaction_id != null && String(product.parent_transaction_id).trim() !== ''
}

function isAutoAddLine(product, ruleId) {
	return (
		!!product?.is_auto_add
		&& Number(product?.auto_add_rule_id) === Number(ruleId)
		&& Number(product?.status) === 0
		&& !product?.done
	)
}

/**
 * На весь заказ — один раз при создании нового заказа.
 * Список правил уже отфильтрован API колл-центра (с собой / доставка).
 */
export function applyWholeOrderAutoAdd(dataCopy, rules, catalogProducts, prepareProductToAdd) {
	if (!Array.isArray(rules) || !rules.length) return dataCopy
	let next = dataCopy

	for (let i = 0; i < rules.length; i++) {
		const rule = rules[i]
		if (resolveAutoAddTypeValue(rule) !== AUTO_ADD_TYPE_VALUE.WHOLE_ORDER) continue

		const already = (next.products || []).some((product) => (
			Number(product.product_id) === Number(rule.product_id)
			&& Number(product.status) === 0
			&& !product.done
		))
		if (already) continue

		const catalogProduct = (catalogProducts || []).find(
			(product) => Number(product.product_id) === Number(rule.product_id)
		)
		if (!catalogProduct || typeof prepareProductToAdd !== 'function') continue

		next = prepareProductToAdd(
			{
				...catalogProduct,
				quantity: Number(rule.quantity) || 1,
				is_auto_add: true,
				auto_add_rule_id: rule.id,
				is_required: !!rule.is_required,
			},
			next,
			{ instantlySetQuantity: true }
		)

		const last = next.products[next.products.length - 1]
		if (last && Number(last.product_id) === Number(rule.product_id)) {
			last.is_auto_add = true
			last.auto_add_rule_id = rule.id
			if (rule.is_required) last.is_required = true
		}
	}

	return next
}

/**
 * Для блюд: qty = сумма qty триггеров × rule.quantity.
 * Пересчитывается при каждом изменении корзины.
 */
export function applyForDishesAutoAdd(dataCopy, rules, catalogProducts, prepareProductToAdd) {
	if (!dataCopy || !Array.isArray(dataCopy.products)) return dataCopy
	if (!Array.isArray(rules) || !rules.length) return dataCopy

	let next = dataCopy
	const dishRules = rules.filter(
		(rule) => resolveAutoAddTypeValue(rule) === AUTO_ADD_TYPE_VALUE.FOR_DISHES
	)

	for (let i = 0; i < dishRules.length; i++) {
		const rule = dishRules[i]
		const triggerIds = (rule.trigger_product_ids || []).map(Number).filter((id) => id > 0)
		if (!triggerIds.length) continue

		let triggerQty = 0
		for (let p = 0; p < next.products.length; p++) {
			const line = next.products[p]
			if (!isActiveLine(line) || line.is_auto_add || line.is_promo_gift) continue
			if (isModifierLine(line)) continue
			if (triggerIds.includes(Number(line.product_id))) {
				triggerQty += Number(line.quantity) || 0
			}
		}

		const neededQty = Math.max(0, Math.round(triggerQty * Number(rule.quantity || 1)))
		let lineIndex = next.products.findIndex((product) => isAutoAddLine(product, rule.id))

		if (neededQty <= 0) {
			if (lineIndex >= 0 && !next.products[lineIndex].id) {
				next.products.splice(lineIndex, 1)
			}
			continue
		}

		if (lineIndex >= 0) {
			next.products[lineIndex].quantity = neededQty
			next.products[lineIndex].total_price =
				Number(next.products[lineIndex].sale_price) * neededQty
			next.products[lineIndex].is_auto_add = true
			next.products[lineIndex].auto_add_rule_id = rule.id
			if (rule.is_required) next.products[lineIndex].is_required = true
			continue
		}

		const catalogProduct = (catalogProducts || []).find(
			(product) => Number(product.product_id) === Number(rule.product_id)
		)
		if (!catalogProduct || typeof prepareProductToAdd !== 'function') continue

		next = prepareProductToAdd(
			{
				...catalogProduct,
				quantity: neededQty,
				is_auto_add: true,
				auto_add_rule_id: rule.id,
				is_required: !!rule.is_required,
			},
			next,
			{ instantlySetQuantity: true }
		)

		lineIndex = next.products.findIndex((product) => (
			Number(product.product_id) === Number(rule.product_id)
			&& Number(product.status) === 0
			&& !product.done
			&& (
				Number(product.auto_add_rule_id) === Number(rule.id)
				|| product.is_auto_add
			)
		))
		if (lineIndex < 0) {
			lineIndex = next.products.findIndex((product) => (
				Number(product.product_id) === Number(rule.product_id)
				&& Number(product.status) === 0
				&& !product.done
			))
		}
		if (lineIndex >= 0) {
			next.products[lineIndex].is_auto_add = true
			next.products[lineIndex].auto_add_rule_id = rule.id
			next.products[lineIndex].quantity = neededQty
			next.products[lineIndex].total_price =
				Number(next.products[lineIndex].sale_price) * neededQty
			if (rule.is_required) next.products[lineIndex].is_required = true
		}
	}

	return next
}
